import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Archive,
  ArrowRight,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  Search,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { exportURL, request } from "../lib/api";
import { getAdminToken } from "../lib/auth";
import type { ExportSummary } from "../types";
import {
  Card,
  EmptyState,
  ErrorState,
  InlineNotice,
  LoadingState,
  errorMessage,
  inputClass,
  secondaryButtonClass,
} from "../components/ui";

// The datasets, in groups, each with a title and a plain description. They come
// from the server, so a dataset added there shows up here with its description.
interface DatasetInfo {
  name: string;
  title: string;
  group: string;
  about: string;
  kind: string;
  columns: string[];
}

// Where to start: the raw log is the source; summaries and records are built or
// entered; reference files explain the ids.
const KIND_STYLE: Record<string, string> = {
  "Raw log": "bg-warning-bg text-warning-text",
  Records: "bg-info-bg text-interactive",
  Summary: "bg-success-bg text-success-text",
  Reference: "bg-surface-muted text-content-secondary",
};
function KindTag({ kind }: { kind: string }) {
  return (
    <span
      className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${KIND_STYLE[kind] ?? KIND_STYLE.Reference}`}
    >
      {kind}
    </span>
  );
}

const PAGE_SIZE = 50;
// Ids, dates and short values are always shown whole. Only long text, such as an
// event's payload, wraps inside a wide cell so one value does not widen the table.
const LONG_CELL = 64;

interface RowsPage {
  columns: string[];
  rows: string[][];
  total: number;
  limit: number;
  offset: number;
}

export function Exports() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("dataset");
  const [catalog, setCatalog] = useState<DatasetInfo[] | null>(null);
  const [catalogError, setCatalogError] = useState("");
  const dataset = catalog?.find((item) => item.name === requested) ?? catalog?.[0];
  const datasetName = dataset?.name;
  // Categories open and close like folders. Choosing a dataset (or arriving on
  // one by its link) opens its category, so you can see where you are; any
  // category can still be closed.
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const selectedGroup = dataset?.group;
  useEffect(() => {
    if (selectedGroup) setOpenGroups((current) => new Set(current).add(selectedGroup));
  }, [selectedGroup]);
  function toggleGroup(group: string) {
    setOpenGroups((current) => {
      const next = new Set(current);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }
  const groups = catalog
    ? [...new Set(catalog.map((item) => item.group))].map((group) => ({
        group,
        items: catalog.filter((item) => item.group === group),
      }))
    : [];
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState<ExportSummary | null>(null);
  const [summaryError, setSummaryError] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<RowsPage | null>(null);
  const [pageError, setPageError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    request<{ datasets: DatasetInfo[] }>("/api/admin/exports/catalog")
      .then((data) => setCatalog(data.datasets))
      .catch((cause) => setCatalogError(errorMessage(cause)));
  }, []);

  useEffect(() => {
    request<ExportSummary>("/api/admin/exports/summary")
      .then((data) => {
        setSummary(data);
        setSummaryError("");
      })
      .catch((cause) => setSummaryError(errorMessage(cause)));
  }, []);

  // Typing waits a moment before searching, so each keystroke is not a query.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setOffset(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!datasetName) return undefined;
    let current = true;
    setLoading(true);
    const qs = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (query) qs.set("q", query);
    request<RowsPage>(`/api/admin/exports/${datasetName}/rows?${qs}`)
      .then((data) => {
        if (!current) return;
        setPage(data);
        setPageError("");
      })
      .catch((cause) => {
        if (current) setPageError(errorMessage(cause));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [datasetName, query, offset]);

  function chooseDataset(name: string) {
    setParams({ dataset: name });
    setSearch("");
    setQuery("");
    setOffset(0);
    setPage(null);
  }

  async function download(name: string) {
    setError("");
    setPending(name);
    try {
      const response = await fetch(exportURL(name), {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
      });
      if (!response.ok) throw new Error(`Export failed (${response.status}).`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = name === "all" ? "research-data.zip" : `${name}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(null);
    }
  }

  const total = page?.total ?? 0;
  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + PAGE_SIZE, total);
  return (
    <div className="grid gap-6">
      <Card
        title="Research data"
        text="Live view of the same records the CSV downloads contain. Search looks across every column."
      >
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            className={secondaryButtonClass}
            disabled={pending === "all"}
            onClick={() => void download("all")}
          >
            {pending === "all" ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Archive size={16} />
            )}
            Download everything (ZIP)
          </button>
        </div>
        {catalogError ? (
          <ErrorState message={catalogError} />
        ) : !catalog || !dataset ? (
          <LoadingState message="Loading datasets…" />
        ) : (
          <div className="grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
            <nav aria-label="Datasets" className="grid content-start gap-2">
              {groups.map(({ group, items }) => {
                const open = openGroups.has(group);
                return (
                  <div key={group} className="rounded-lg border border-border bg-surface">
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => toggleGroup(group)}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left transition hover:bg-surface-muted"
                    >
                      <ChevronRight
                        size={15}
                        className={`shrink-0 text-content-secondary transition-transform ${open ? "rotate-90" : ""}`}
                      />
                      <span className="min-w-0 flex-1 text-sm font-semibold text-content-primary">
                        {group}
                      </span>
                      <span className="rounded-md bg-surface-muted px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-content-secondary">
                        {items.length}
                      </span>
                    </button>
                    {open && (
                      <div className="grid gap-1 border-t border-border p-1.5">
                        {items.map((item) => {
                          const active = item.name === dataset.name;
                          return (
                            <button
                              key={item.name}
                              type="button"
                              aria-current={active ? "true" : undefined}
                              onClick={() => chooseDataset(item.name)}
                              title={item.about}
                              className={`rounded-md border px-3 py-2 text-left transition ${
                                active
                                  ? "border-interactive/40 bg-primary-soft"
                                  : "border-transparent hover:bg-surface-muted"
                              }`}
                            >
                              <span className="flex items-center justify-between gap-2">
                                <span
                                  className={`min-w-0 text-sm font-medium ${active ? "text-interactive" : "text-content-primary"}`}
                                >
                                  {item.title}
                                </span>
                                <KindTag kind={item.kind} />
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
            <div className="min-w-0">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-semibold tracking-tight text-content-primary">
                      {dataset.title}
                    </h3>
                    <KindTag kind={dataset.kind} />
                  </div>
                  <p className="mt-1 max-w-3xl text-sm leading-6 text-content-secondary">
                    {dataset.about}
                  </p>
                  <p className="mt-1 text-xs text-content-disabled">
                    {dataset.group} · {dataset.columns.length} columns
                  </p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <div className="relative min-w-0 flex-1 sm:max-w-sm">
                  <Search
                    size={16}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-disabled"
                  />
                  <input
                    className={`${inputClass} pl-9`}
                    placeholder="Search this dataset"
                    aria-label="Search this dataset"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className={secondaryButtonClass}
                  disabled={pending === dataset.name}
                  onClick={() => void download(dataset.name)}
                >
                  {pending === dataset.name ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Download size={16} />
                  )}
                  Download CSV
                </button>
              </div>
              {error && <InlineNotice tone="error" text={error} />}
              <div className="mt-4">
                {pageError ? (
                  <ErrorState message={pageError} />
                ) : !page ? (
                  <LoadingState message="Loading records…" />
                ) : page.rows.length === 0 ? (
                  <EmptyState
                    title={query ? "No matching records" : "No records yet"}
                    message={
                      query
                        ? "Nothing in this dataset contains that text."
                        : "Nothing has been recorded in this dataset yet."
                    }
                  />
                ) : (
                  <div
                    className={`max-h-[60vh] overflow-auto rounded-md border border-border ${loading ? "opacity-60" : ""}`}
                  >
                    <table className="w-full border-separate border-spacing-0 text-start text-sm">
                      <thead>
                        <tr>
                          {page.columns.map((column) => (
                            <th
                              key={column}
                              className="sticky top-0 whitespace-nowrap border-b border-border bg-surface-muted px-3 py-2 text-start text-xs font-semibold uppercase tracking-wide text-content-secondary"
                            >
                              {column.replaceAll("_", " ")}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {page.rows.map((row, index) => (
                          <tr key={`${offset}-${index}`} className="hover:bg-surface-muted/50">
                            {row.map((cell, column) => (
                              <td
                                key={column}
                                title={cell}
                                className={`border-b border-border px-3 py-2 font-mono text-xs text-content-primary ${
                                  cell.length > LONG_CELL
                                    ? "min-w-[20rem] max-w-[32rem] whitespace-normal break-words"
                                    : "whitespace-nowrap"
                                }`}
                              >
                                {cell === "" ? (
                                  <span className="text-content-disabled">—</span>
                                ) : (
                                  cell
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              {page && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-content-secondary">
                  <span>
                    {total === 0
                      ? "0 records"
                      : `${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()} records`}
                  </span>
                  <span className="flex gap-2">
                    <button
                      type="button"
                      className={secondaryButtonClass}
                      disabled={offset === 0 || loading}
                      onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    >
                      <ChevronLeft size={16} /> Previous
                    </button>
                    <button
                      type="button"
                      className={secondaryButtonClass}
                      disabled={offset + PAGE_SIZE >= total || loading}
                      onClick={() => setOffset(offset + PAGE_SIZE)}
                    >
                      Next <ChevronRight size={16} />
                    </button>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
      <Card title="Programme snapshot" text="Current aggregate counts across the programme.">
        {summaryError ? (
          <ErrorState message={summaryError} />
        ) : !summary ? (
          <LoadingState message="Loading summary…" />
        ) : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <SummaryStat
              icon={UsersRound}
              tone="teal"
              label="Active users"
              value={summary.active_users}
            />
            <SummaryStat
              icon={UsersRound}
              tone="crimson"
              label="Teachers"
              value={summary.teachers}
            />
            <SummaryStat icon={ShieldCheck} tone="blue" label="Admins" value={summary.admins} />
            <SummaryStat
              icon={FileText}
              tone="slate"
              label="Content items"
              value={summary.content_items}
            />
            <SummaryStat
              icon={BookOpen}
              tone="teal"
              label="Draft modules"
              value={summary.modules_by_status.draft}
            />
            <SummaryStat
              icon={BookOpen}
              tone="crimson"
              label="Published modules"
              value={summary.modules_by_status.published}
            />
            <SummaryStat
              icon={BookOpen}
              tone="slate"
              label="Archived modules"
              value={summary.modules_by_status.archived}
            />
            <SummaryStat
              icon={ArrowRight}
              tone="blue"
              label="Avg completion"
              value={
                summary.avg_completion_percent === null
                  ? "—"
                  : `${summary.avg_completion_percent.toFixed(1)}%`
              }
            />
          </div>
        )}
      </Card>
    </div>
  );
}

function SummaryStat({
  icon: Icon,
  tone,
  label,
  value,
}: {
  icon: typeof UsersRound;
  tone: string;
  label: string;
  value: number | string;
}) {
  return (
    <div className="flex min-h-28 items-center gap-4 rounded-lg border border-border bg-surface p-4 shadow-sm">
      <span className={`metric-icon metric-icon-${tone}`}>
        <Icon size={20} strokeWidth={1.8} />
      </span>
      <span className="min-w-0 flex-1">
        <strong className="block text-xl font-bold tabular-nums text-content-primary">
          {typeof value === "number" ? value.toLocaleString() : value}
        </strong>
        <span className="mt-1 block text-sm text-content-secondary">{label}</span>
      </span>
    </div>
  );
}
